import React, { useState, useMemo } from 'react';
import { 
  FileText, 
  Download, 
  ExternalLink, 
  Search, 
  Eye, 
  EyeOff, 
  Layers, 
  Sparkles,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
  ArrowUpRight
} from 'lucide-react';
import { COMMITTEE_MATRICES, MATRICES_PDF_URL, CommitteeMatrix } from '../data/matricesData';

interface MatricesSectionProps {
  reducedMotion: boolean;
  onSelectCommitteeForRegistration?: (committeeAbbr: string) => void;
}

type CategoryFilter = 'ALL' | 'UN' | 'Indian' | 'Crisis' | 'Auction' | 'Press';

export const MatricesSection: React.FC<MatricesSectionProps> = ({ 
  reducedMotion,
  onSelectCommitteeForRegistration 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('ALL');
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [expandedCommittees, setExpandedCommittees] = useState<Record<string, boolean>>({
    'unga': true,
    'ecosoc': true,
    'lok-sabha': true,
    'aippm': true,
    'dead-poets-society': true,
    'rajputana-yuddh-parishad': true,
    'ipl': true,
    'fifa': true,
    'ip': true,
  });

  const toggleExpand = (id: string) => {
    setExpandedCommittees(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const expandAll = () => {
    const allExpanded: Record<string, boolean> = {};
    COMMITTEE_MATRICES.forEach(c => { allExpanded[c.id] = true; });
    setExpandedCommittees(allExpanded);
  };

  const collapseAll = () => {
    const allCollapsed: Record<string, boolean> = {};
    COMMITTEE_MATRICES.forEach(c => { allCollapsed[c.id] = false; });
    setExpandedCommittees(allCollapsed);
  };

  // Filtered committees based on category and search query
  const filteredCommittees = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    
    return COMMITTEE_MATRICES.map(committee => {
      // Check category match
      const categoryMatch = activeCategory === 'ALL' || committee.categoryGroup === activeCategory;
      if (!categoryMatch) return null;

      if (!query) {
        return { ...committee, matchingPortfolios: committee.portfolios };
      }

      // If search query exists, filter portfolios or match committee name
      const nameMatch = committee.name.toLowerCase().includes(query) || 
                         committee.category.toLowerCase().includes(query);

      const matchingPortfolios = committee.portfolios.filter(p => 
        p.toLowerCase().includes(query)
      );

      if (nameMatch || matchingPortfolios.length > 0) {
        return {
          ...committee,
          matchingPortfolios: nameMatch ? committee.portfolios : matchingPortfolios
        };
      }

      return null;
    }).filter(Boolean) as (CommitteeMatrix & { matchingPortfolios: string[] })[];
  }, [searchQuery, activeCategory]);



  return (
    <section 
      id="matrices" 
      className="relative py-24 sm:py-32 bg-gradient-to-b from-deepblack via-[#0e0d0c] to-deepblack border-t border-white/5 overflow-hidden"
    >
      {/* Background Architectural Watermark */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02] flex items-center justify-center font-serif text-[24vw] font-bold text-beige select-none">
        MATRICES
      </div>

      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32 relative z-10">
        
        {/* Pre-title & Classical Rule */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-[1px] bg-bronze" />
          <span className="text-xs uppercase tracking-architectural text-bronze font-semibold">
            OFFICIAL ALLOTMENTS • COUNTRY & PORTFOLIO MATRICES
          </span>
          <div className="w-12 h-[1px] bg-bronze" />
        </div>

        {/* Section Heading & Download Card */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-bronze/40 rounded-sm mb-3">
              <Sparkles className="w-3.5 h-3.5 text-beige" />
              <span className="text-[10px] uppercase font-mono tracking-widest text-beige font-bold">
                FLSxMUN 2.0 • FOR THE YOUTH BY THE YOUTH
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ivory">
              COMMITTEE <br />
              <span className="text-beige font-light italic">MATRICES</span>
            </h2>
            <p className="text-sm text-ivory/70 font-light max-w-2xl mt-4 leading-relaxed">
              Explore the official country allotments, parliamentary seats, historical continuous crisis roles, 
              and sports franchise auctions across all 9 chambers.
            </p>
          </div>

          {/* Quick PDF Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 shrink-0">
            <a
              href={MATRICES_PDF_URL}
              download="FLSxMUN_2.0_Matrices.pdf"
              className="px-6 py-3.5 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack font-mono text-xs uppercase tracking-widest font-bold rounded-sm border border-beige hover:shadow-glow-bronze transition-all flex items-center justify-center gap-2.5 shadow-lg group"
              data-cursor-interactive
              data-cursor-text="PDF"
            >
              <Download className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" />
              <span>DOWNLOAD OFFICIAL PDF (2.1 MB)</span>
            </a>

            <div className="flex items-center gap-2">
              <a
                href={MATRICES_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 sm:flex-initial px-4 py-3.5 bg-white/5 border border-bronze/40 text-beige hover:bg-white/10 hover:border-beige transition-all text-xs font-mono uppercase tracking-widest font-semibold rounded-sm flex items-center justify-center gap-2"
                data-cursor-interactive
                data-cursor-text="OPEN"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>OPEN IN TAB</span>
              </a>

              <button
                type="button"
                onClick={() => setShowPdfViewer(prev => !prev)}
                className="px-4 py-3.5 bg-white/5 border border-bronze/40 text-ivory/80 hover:text-white hover:border-beige transition-all text-xs font-mono uppercase tracking-widest font-semibold rounded-sm flex items-center justify-center gap-2"
                data-cursor-interactive
                data-cursor-text="PREVIEW"
              >
                {showPdfViewer ? <EyeOff className="w-3.5 h-3.5 text-beige" /> : <Eye className="w-3.5 h-3.5 text-bronze" />}
                <span>{showPdfViewer ? 'HIDE VIEWER' : 'PREVIEW PDF'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* In-Page PDF Viewer Drawer (Collapsible) */}
        {showPdfViewer && (
          <div className="mb-12 p-4 bg-charcoal-subtle/80 border border-bronze/50 rounded-sm shadow-2xl animate-fadeIn">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/10 text-xs font-mono text-beige uppercase tracking-wider">
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-bronze" />
                FLSxMUN 2.0 OFFICIAL MATRICES DOCUMENT PREVIEW
              </span>
              <a 
                href={MATRICES_PDF_URL} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-ivory/60 hover:text-white flex items-center gap-1 text-[11px]"
              >
                Fullscreen ↗
              </a>
            </div>
            <div className="relative w-full h-[600px] sm:h-[750px] bg-black rounded-sm overflow-hidden border border-white/5">
              <iframe
                src={`${MATRICES_PDF_URL}#toolbar=1&navpanes=0`}
                title="FLSxMUN 2.0 Committee Matrices PDF"
                className="w-full h-full border-0"
              />
            </div>
          </div>
        )}

        {/* Search Bar & Filter Controls */}
        <div className="p-4 sm:p-5 bg-charcoal-subtle/60 border border-bronze/30 rounded-sm mb-10 shadow-lg">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
            
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-bronze absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search countries, leaders, or clubs (e.g., India, Modi, Barcelona, Keating)..."
                className="w-full pl-10 pr-4 py-2.5 bg-black/60 border border-white/10 rounded-sm text-xs sm:text-sm text-ivory placeholder-ivory/30 focus:outline-none focus:border-beige transition-colors font-mono"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-ivory/40 hover:text-ivory"
                >
                  CLEAR
                </button>
              )}
            </div>

            {/* Expand / Collapse All Quick Toggles */}
            <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest uppercase self-end md:self-auto">
              <button
                type="button"
                onClick={expandAll}
                className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm text-ivory/70 hover:text-beige transition-colors"
              >
                Expand All
              </button>
              <button
                type="button"
                onClick={collapseAll}
                className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm text-ivory/70 hover:text-beige transition-colors"
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 sm:gap-2 mt-4 pt-4 border-t border-white/5 overflow-x-auto pb-1 scrollbar-none">
            {(
              [
                { id: 'ALL', label: 'ALL 09 COMMITTEES' },
                { id: 'UN', label: 'UNITED NATIONS' },
                { id: 'Indian', label: 'INDIAN PARLIAMENTARY' },
                { id: 'Crisis', label: 'CRISIS & CCC' },
                { id: 'Auction', label: 'AUCTION TRACK' },
                { id: 'Press', label: 'PRESS CORPS' },
              ] as const
            ).map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono rounded-sm transition-all whitespace-nowrap shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-beige text-deepblack font-bold border border-beige shadow-sm'
                    : 'bg-white/5 border border-white/5 text-ivory/60 hover:text-ivory hover:border-bronze/40'
                }`}
                data-cursor-interactive
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Result Status & Found Count */}
        {searchQuery && (
          <div className="mb-6 flex items-center justify-between text-xs font-mono text-beige/80">
            <span>
              Showing results for "{searchQuery}" across {filteredCommittees.length} committee{filteredCommittees.length === 1 ? '' : 's'}
            </span>
            <span>
              {filteredCommittees.reduce((acc, c) => acc + c.matchingPortfolios.length, 0)} results found
            </span>
          </div>
        )}

        {/* Committees Matrices Accordion / Grid */}
        <div className="space-y-6">
          {filteredCommittees.length === 0 ? (
            <div className="p-12 text-center marble-card border border-bronze/30 rounded-sm">
              <ShieldAlert className="w-10 h-10 text-bronze mx-auto mb-3 opacity-60" />
              <p className="font-serif text-lg text-ivory mb-1">No Matching Allotments Found</p>
              <p className="text-xs text-ivory/50 font-mono">
                No chamber allotments matched your query "{searchQuery}". Try searching for another country or leader.
              </p>
              <button
                type="button"
                onClick={() => { setSearchQuery(''); setActiveCategory('ALL'); }}
                className="mt-4 px-4 py-2 bg-beige/10 border border-beige/40 text-beige text-xs uppercase font-mono tracking-widest rounded-sm hover:bg-beige hover:text-deepblack transition-colors"
              >
                Reset Search Filters
              </button>
            </div>
          ) : (
            filteredCommittees.map((committee) => {
              const isExpanded = !!expandedCommittees[committee.id];
              const displayPortfolios = committee.matchingPortfolios;

              return (
                <div 
                  key={committee.id}
                  className="marble-card border border-bronze/40 rounded-sm overflow-hidden transition-all duration-300 hover:border-beige/60 shadow-lg"
                >
                  {/* Committee Matrix Card Header */}
                  <div 
                    onClick={() => toggleExpand(committee.id)}
                    className="p-5 sm:p-6 bg-charcoal-subtle/70 flex items-center justify-between gap-4 cursor-pointer select-none group border-b border-white/5 hover:bg-charcoal-subtle/90 transition-colors"
                    data-cursor-interactive
                  >
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 overflow-hidden">
                      <div className="w-10 h-10 rounded-sm bg-black/60 border border-bronze/40 flex items-center justify-center shrink-0 text-bronze group-hover:text-beige group-hover:border-beige transition-colors">
                        <Layers className="w-5 h-5" />
                      </div>
                      <div className="overflow-hidden">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-bronze">
                            {committee.category}
                          </span>
                          <span className="text-white/20">•</span>
                          <span className="text-[10px] font-mono tracking-widest text-beige-300">
                            {committee.track === 'Auction' ? 'AUCTION TRACK' : 'CONVENTIONAL TRACK'}
                          </span>
                        </div>
                        <h3 className="font-serif text-lg sm:text-xl font-bold text-ivory group-hover:text-beige transition-colors truncate">
                          {committee.name}
                        </h3>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-ivory/60 group-hover:border-beige group-hover:text-beige transition-all">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Portfolios Grid */}
                  {isExpanded && (
                    <div className="p-5 sm:p-6 bg-deepblack/60">
                      {/* Portfolios Pill / Tile Layout */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                        {displayPortfolios.map((item, idx) => {
                          const isHighlighted = searchQuery.trim().length > 0 && 
                                                item.toLowerCase().includes(searchQuery.trim().toLowerCase());

                          return (
                            <div
                              key={item + idx}
                              className={`p-3 rounded-sm border text-xs flex items-center transition-all ${
                                isHighlighted
                                  ? 'bg-beige/15 border-beige text-beige font-semibold shadow-[0_0_15px_rgba(212,175,55,0.25)]'
                                  : 'bg-charcoal-subtle/40 border-white/5 hover:border-bronze/40 text-ivory/85 hover:text-white'
                              }`}
                            >
                              <span className="font-sans text-xs tracking-wide leading-tight truncate">
                                {item}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Committee Footer Action: Direct Registration Link */}
                      <div className="mt-6 pt-4 border-t border-white/5 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 text-xs">
                        <div className="flex items-center gap-3">
                          {onSelectCommitteeForRegistration && (
                            <button
                              type="button"
                              onClick={() => onSelectCommitteeForRegistration(committee.name)}
                              className="px-3.5 py-1.5 bg-white/5 hover:bg-white/10 border border-bronze/30 text-beige hover:border-beige font-mono text-[11px] uppercase tracking-wider rounded-sm transition-colors flex items-center gap-1.5"
                            >
                              <span>SELECT IN REGISTRATION</span>
                              <ArrowUpRight className="w-3.5 h-3.5" />
                            </button>
                          )}

                          <a
                            href={committee.formUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-1.5 bg-beige text-deepblack font-mono text-[11px] font-bold uppercase tracking-wider rounded-sm hover:bg-beige-300 transition-colors flex items-center gap-1.5 shadow-sm"
                            data-cursor-interactive
                          >
                            <span>APPLY FOR THIS CHAMBER</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Bottom Classical Meander */}
        <div className="mt-16 w-full h-2 greek-key-border opacity-30" />

      </div>
    </section>
  );
};
